import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const ALL_PROJECTS_FOR_DROPDOWN = [
  { id: 'dream_kokan', name: 'Dream Kokan City', type: 'land' },
  { id: 'dream_nagpur', name: 'Dream Nagpur City', type: 'land' },
  { id: 'brahma_heights', name: 'Brahma Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'godavari_heights', name: 'Godavari Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'siddhivinayak_heights', name: 'Siddhivinayak Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'kaveri_heights', name: 'Kaveri Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'dream_riviera_a', name: "Dream Riviera 'A' Wing", type: 'flat' },
  { id: 'dream_riviera_b', name: "Dream Riviera 'B' Wing", type: 'flat' },
  { id: 'sai_plaza', name: 'Sai Plaza', type: 'flat' }
];

const PROJECT_CATEGORIES = [
  { id: 'dream_kokan', name: 'Dream Kokan City', color: '#3b82f6', type: 'land' },
  { id: 'dream_nagpur', name: 'Dream Nagpur City', color: '#f97316', type: 'land' },
  { id: 'siddhivinayak_infra', name: 'Siddhivinayak Infra', color: '#10b981', type: 'flat',
    subProjects: [
      { id: 'brahma_heights', name: 'Brahma Heights' },
      { id: 'godavari_heights', name: 'Godavari Heights' },
      { id: 'siddhivinayak_heights', name: 'Siddhivinayak Heights' },
      { id: 'kaveri_heights', name: 'Kaveri Heights' }
    ]
  },
  { id: 'dream_riviera_a', name: "Dream Riviera 'A' Wing", color: '#8b5cf6', type: 'flat' },
  { id: 'dream_riviera_b', name: "Dream Riviera 'B' Wing", color: '#ec4899', type: 'flat' },
  { id: 'sai_plaza', name: 'Sai Plaza', color: '#14b8a6', type: 'flat' }
];

const MarketingDMTab = ({
  selectedProject,
  dmDashboardView, setDmDashboardView,
  allDmData, allDmGrandTotal,
  projectDmPerformance, projectDmGrandTotal,
  allGuntheData, requirementWiseData,
  allAddressData, addressWiseData,
  agentWiseData, referenceWiseData,
  projectSummary, projectGrandTotal,
  openQuickAddModal
}) => {
  const isLandProject = selectedProject === 'dream_kokan' || selectedProject === 'dream_nagpur';
  const isAllProjects = selectedProject === 'all_flat';

  const projectColor = (PROJECT_CATEGORIES.find(p => p.id === selectedProject) || PROJECT_CATEGORIES.find(p => p.subProjects?.some(sp => sp.id === selectedProject)))?.color;
  const projectLabel = selectedProject === 'all_flat'
    ? 'ALL PROJECTS DASHBOARD'
    : `${(ALL_PROJECTS_FOR_DROPDOWN.find(p => p.id === selectedProject)?.name || selectedProject).toUpperCase().replace(' (SIDDHIVINAYAK INFRA)', '')} DASHBOARD`;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-xl font-bold" style={{ color: projectColor }}>
            {projectLabel}
          </CardTitle>
          <div className="flex items-center gap-4">
            <div className="flex flex-wrap gap-1 bg-stone-100 p-1 rounded-lg">
              {(isLandProject || isAllProjects) && (
                <Button size="sm" variant={dmDashboardView === 'agent' ? 'default' : 'ghost'} onClick={() => setDmDashboardView('agent')} data-testid="view-dm-btn" className={dmDashboardView === 'agent' ? 'bg-amber-700 hover:bg-amber-800' : ''}>DM Wise</Button>
              )}
              {(isLandProject || isAllProjects) && (
                <Button size="sm" variant={dmDashboardView === 'requirement' ? 'default' : 'ghost'} onClick={() => setDmDashboardView('requirement')} data-testid="view-gunthe-btn" className={dmDashboardView === 'requirement' ? 'bg-amber-700 hover:bg-amber-800' : ''}>Gunthe Wise</Button>
              )}
              {(isLandProject || isAllProjects) && (
                <Button size="sm" variant={dmDashboardView === 'reference' ? 'default' : 'ghost'} onClick={() => setDmDashboardView('reference')} data-testid="view-address-btn" className={dmDashboardView === 'reference' ? 'bg-amber-700 hover:bg-amber-800' : ''}>Address Wise</Button>
              )}
              {!isLandProject && (
                <Button size="sm" variant={dmDashboardView === 'agent_flat' ? 'default' : 'ghost'} onClick={() => setDmDashboardView('agent_flat')} data-testid="view-agent-btn">Agent-wise</Button>
              )}
              {!isLandProject && (
                <Button size="sm" variant={dmDashboardView === 'requirement_flat' ? 'default' : 'ghost'} onClick={() => setDmDashboardView('requirement_flat')} data-testid="view-requirement-btn">Requirement/Config-wise</Button>
              )}
              {!isLandProject && (
                <Button size="sm" variant={dmDashboardView === 'reference_flat' ? 'default' : 'ghost'} onClick={() => setDmDashboardView('reference_flat')} data-testid="view-reference-btn">Reference-wise</Button>
              )}
            </div>
            <div className="text-sm text-stone-500">
              DATE: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* DM Wise View — land projects and All Projects */}
        {dmDashboardView === 'agent' && (
          <>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={(isAllProjects ? allDmData : projectDmPerformance).slice(0, 10).map(dm => ({
                name: (dm.dmName || dm.name || 'Unknown').split(' ')[0],
                totalLead: dm.totalLead, totalOfficeVisit: dm.totalOfficeVisit,
                totalProjectVisit: dm.totalProjectVisit, totalScopeOfCustomer: dm.totalScopeOfCustomer, totalBooking: dm.totalBooking
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={10} angle={-45} textAnchor="end" height={60} />
                <YAxis /><Tooltip /><Legend />
                <Bar isAnimationActive={false} dataKey="totalLead" fill="#1d4ed8" name="Total Lead" />
                <Bar isAnimationActive={false} dataKey="totalOfficeVisit" fill="#f97316" name="Office Visit" />
                <Bar isAnimationActive={false} dataKey="totalProjectVisit" fill="#15803d" name="Project Visit" />
                <Bar isAnimationActive={false} dataKey="totalScopeOfCustomer" fill="#eab308" name="Scope" />
                <Bar isAnimationActive={false} dataKey="totalBooking" fill="#8b5cf6" name="Booking" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm border" data-testid="dm-wise-land-table">
                <thead>
                  <tr className="bg-blue-800 text-white">
                    <th className="px-3 py-2 text-left border">DM NAME</th>
                    <th className="px-3 py-2 text-right border">TOTAL LEAD</th>
                    <th className="px-3 py-2 text-right border">TOTAL OFFICE VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL PROJECT VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL SCOPE OF CUSTOMER</th>
                    <th className="px-3 py-2 text-right border">TOTAL BOOKING</th>
                  </tr>
                </thead>
                <tbody>
                  {(isAllProjects ? allDmData : projectDmPerformance).map((dm, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                      <td className="px-3 py-2 border font-medium">{dm.dmName || dm.name}</td>
                      <td className="px-3 py-2 border text-right font-bold text-blue-700">{dm.totalLead?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{dm.totalOfficeVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{dm.totalProjectVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{dm.totalScopeOfCustomer?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right font-bold text-emerald-700">{dm.totalBooking?.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr className="bg-blue-100 font-bold">
                    <td className="px-3 py-2 border text-blue-800">GRAND TOTAL</td>
                    <td className="px-3 py-2 border text-right text-blue-800">{(isAllProjects ? allDmGrandTotal : projectDmGrandTotal)?.totalLead?.toLocaleString() || 0}</td>
                    <td className="px-3 py-2 border text-right text-blue-800">{(isAllProjects ? allDmGrandTotal : projectDmGrandTotal)?.totalOfficeVisit?.toLocaleString() || 0}</td>
                    <td className="px-3 py-2 border text-right text-blue-800">{(isAllProjects ? allDmGrandTotal : projectDmGrandTotal)?.totalProjectVisit?.toLocaleString() || 0}</td>
                    <td className="px-3 py-2 border text-right text-blue-800">{(isAllProjects ? allDmGrandTotal : projectDmGrandTotal)?.totalScopeOfCustomer?.toLocaleString() || 0}</td>
                    <td className="px-3 py-2 border text-right text-emerald-800">{(isAllProjects ? allDmGrandTotal : projectDmGrandTotal)?.totalBooking?.toLocaleString() || 0}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Gunthe Wise View */}
        {dmDashboardView === 'requirement' && (
          <>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={(isAllProjects ? allGuntheData : requirementWiseData).slice(0, 12).map(req => ({
                name: req.gunthe || '-',
                totalLead: req.totalLead, totalOfficeVisit: req.totalOfficeVisit,
                totalProjectVisit: req.totalProjectVisit, totalScopeOfCustomer: req.totalScopeOfCustomer, totalBooking: req.totalBooking
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={10} angle={-30} textAnchor="end" height={60} />
                <YAxis /><Tooltip /><Legend />
                <Bar isAnimationActive={false} dataKey="totalLead" fill="#f97316" name="Total Lead" />
                <Bar isAnimationActive={false} dataKey="totalOfficeVisit" fill="#06b6d4" name="Office Visit" />
                <Bar isAnimationActive={false} dataKey="totalProjectVisit" fill="#15803d" name="Project Visit" />
                <Bar isAnimationActive={false} dataKey="totalScopeOfCustomer" fill="#eab308" name="Scope" />
                <Bar isAnimationActive={false} dataKey="totalBooking" fill="#8b5cf6" name="Booking" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm border" data-testid="gunthe-wise-table">
                <thead>
                  <tr className="bg-orange-700 text-white">
                    <th className="px-3 py-2 text-left border">GUNTHE</th>
                    <th className="px-3 py-2 text-right border">TOTAL LEAD</th>
                    <th className="px-3 py-2 text-right border">TOTAL OFFICE VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL PROJECT VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL SCOPE OF CUSTOMER</th>
                    <th className="px-3 py-2 text-right border">TOTAL BOOKING</th>
                  </tr>
                </thead>
                <tbody>
                  {(isAllProjects ? allGuntheData : requirementWiseData).length > 0
                    ? (isAllProjects ? allGuntheData : requirementWiseData).map((req, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                        <td className="px-3 py-2 border font-medium">{req.gunthe}</td>
                        <td className="px-3 py-2 border text-right font-bold text-orange-700">{req.totalLead?.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{req.totalOfficeVisit?.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{req.totalProjectVisit?.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{req.totalScopeOfCustomer?.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right font-bold text-emerald-700">{req.totalBooking?.toLocaleString()}</td>
                      </tr>
                    ))
                    : <tr><td colSpan="6" className="px-3 py-4 text-center text-stone-500">No data available</td></tr>
                  }
                  {(isAllProjects ? allGuntheData : requirementWiseData).length > 0 && (
                    <tr className="bg-orange-100 font-bold">
                      <td className="px-3 py-2 border text-orange-800">GRAND TOTAL</td>
                      {['totalLead','totalOfficeVisit','totalProjectVisit','totalScopeOfCustomer','totalBooking'].map((k, ki) => (
                        <td key={ki} className={`px-3 py-2 border text-right ${k==='totalBooking'?'text-emerald-800':'text-orange-800'}`}>
                          {(isAllProjects ? allGuntheData : requirementWiseData).reduce((a, b) => a + (b[k] || 0), 0).toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Address Wise View */}
        {dmDashboardView === 'reference' && (
          <>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={(isAllProjects ? allAddressData : addressWiseData).slice(0, 12).map(addr => ({
                name: addr.address || '-',
                totalLead: addr.totalLead, totalOfficeVisit: addr.totalOfficeVisit,
                totalProjectVisit: addr.totalProjectVisit, totalScopeOfCustomer: addr.totalScopeOfCustomer, totalBooking: addr.totalBooking
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={10} angle={-30} textAnchor="end" height={70} interval={0} />
                <YAxis /><Tooltip /><Legend />
                <Bar isAnimationActive={false} dataKey="totalLead" fill="#b45309" name="Total Lead" />
                <Bar isAnimationActive={false} dataKey="totalOfficeVisit" fill="#f97316" name="Office Visit" />
                <Bar isAnimationActive={false} dataKey="totalProjectVisit" fill="#15803d" name="Project Visit" />
                <Bar isAnimationActive={false} dataKey="totalScopeOfCustomer" fill="#eab308" name="Scope" />
                <Bar isAnimationActive={false} dataKey="totalBooking" fill="#8b5cf6" name="Booking" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm border" data-testid="address-wise-table">
                <thead>
                  <tr className="bg-amber-700 text-white">
                    <th className="px-3 py-2 text-left border">ADDRESS</th>
                    <th className="px-3 py-2 text-right border">TOTAL LEAD</th>
                    <th className="px-3 py-2 text-right border">TOTAL OFFICE VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL PROJECT VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL SCOPE OF CUSTOMER</th>
                    <th className="px-3 py-2 text-right border">TOTAL BOOKING</th>
                  </tr>
                </thead>
                <tbody>
                  {(isAllProjects ? allAddressData : addressWiseData).length > 0
                    ? (isAllProjects ? allAddressData : addressWiseData).map((addr, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                        <td className="px-3 py-2 border font-medium">{addr.address}</td>
                        <td className="px-3 py-2 border text-right font-bold text-amber-700">{addr.totalLead?.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{addr.totalOfficeVisit?.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{addr.totalProjectVisit?.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{addr.totalScopeOfCustomer?.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right font-bold text-emerald-700">{addr.totalBooking?.toLocaleString()}</td>
                      </tr>
                    ))
                    : <tr><td colSpan="6" className="px-3 py-4 text-center text-stone-500">No data available</td></tr>
                  }
                  {(isAllProjects ? allAddressData : addressWiseData).length > 0 && (
                    <tr className="bg-amber-100 font-bold">
                      <td className="px-3 py-2 border text-amber-800">GRAND TOTAL</td>
                      {['totalLead','totalOfficeVisit','totalProjectVisit','totalScopeOfCustomer','totalBooking'].map((k, ki) => (
                        <td key={ki} className={`px-3 py-2 border text-right ${k==='totalBooking'?'text-emerald-800':'text-amber-800'}`}>
                          {(isAllProjects ? allAddressData : addressWiseData).reduce((a, b) => a + (b[k] || 0), 0).toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Agent-wise View (flat projects) */}
        {dmDashboardView === 'agent_flat' && (
          <>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={projectDmPerformance.slice(0, 10).map(dm => ({
                name: dm.dmName?.split(' ')[0] || 'Unknown',
                totalLead: dm.totalLead, totalOfficeVisit: dm.totalOfficeVisit,
                totalProjectVisit: dm.totalProjectVisit, totalScopeOfCustomer: dm.totalScopeOfCustomer, totalBooking: dm.totalBooking
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={10} angle={-45} textAnchor="end" height={60} />
                <YAxis /><Tooltip /><Legend />
                <Bar isAnimationActive={false} dataKey="totalLead" fill="#1d4ed8" name="Total Lead" />
                <Bar isAnimationActive={false} dataKey="totalOfficeVisit" fill="#f97316" name="Office Visit" />
                <Bar isAnimationActive={false} dataKey="totalProjectVisit" fill="#15803d" name="Project Visit" />
                <Bar isAnimationActive={false} dataKey="totalScopeOfCustomer" fill="#eab308" name="Scope" />
                <Bar isAnimationActive={false} dataKey="totalBooking" fill="#8b5cf6" name="Booking" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm border" data-testid="dm-performance-table">
                <thead>
                  <tr className="bg-blue-800 text-white">
                    <th className="px-3 py-2 text-left border">DM NAME</th>
                    <th className="px-3 py-2 text-right border">TOTAL LEAD</th>
                    <th className="px-3 py-2 text-right border">TOTAL OFFICE VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL PROJECT VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL SCOPE OF CUSTOMER</th>
                    <th className="px-3 py-2 text-right border">TOTAL BOOKING</th>
                  </tr>
                </thead>
                <tbody>
                  {projectDmPerformance.map((dm, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                      <td className="px-3 py-2 border font-medium">{dm.dmName}</td>
                      <td className="px-3 py-2 border text-right font-bold text-blue-700">{dm.totalLead?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{dm.totalOfficeVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{dm.totalProjectVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{dm.totalScopeOfCustomer?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right font-bold text-emerald-700">{dm.totalBooking?.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr className="bg-blue-100 font-bold">
                    <td className="px-3 py-2 border text-blue-800">GRAND TOTAL</td>
                    <td className="px-3 py-2 border text-right text-blue-800">{projectDmGrandTotal?.totalLead?.toLocaleString() || 0}</td>
                    <td className="px-3 py-2 border text-right text-blue-800">{projectDmGrandTotal?.totalOfficeVisit?.toLocaleString() || 0}</td>
                    <td className="px-3 py-2 border text-right text-blue-800">{projectDmGrandTotal?.totalProjectVisit?.toLocaleString() || 0}</td>
                    <td className="px-3 py-2 border text-right text-blue-800">{projectDmGrandTotal?.totalScopeOfCustomer?.toLocaleString() || 0}</td>
                    <td className="px-3 py-2 border text-right text-emerald-800">{projectDmGrandTotal?.totalBooking?.toLocaleString() || 0}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Requirement/Config-wise View (flat projects) */}
        {dmDashboardView === 'requirement_flat' && (
          <>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={requirementWiseData.filter(req => req.requirement && req.requirement !== 'UNKNOWN').slice(0, 12).map(req => ({
                name: req.requirement || '-',
                totalLead: req.totalLead, totalOfficeVisit: req.totalOfficeVisit,
                totalProjectVisit: req.totalProjectVisit, totalScopeOfCustomer: req.totalScopeOfCustomer, totalBooking: req.totalBooking
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={10} angle={-30} textAnchor="end" height={60} />
                <YAxis /><Tooltip /><Legend />
                <Bar isAnimationActive={false} dataKey="totalLead" fill="#0d9488" name="Total Lead" />
                <Bar isAnimationActive={false} dataKey="totalOfficeVisit" fill="#f97316" name="Office Visit" />
                <Bar isAnimationActive={false} dataKey="totalProjectVisit" fill="#15803d" name="Project Visit" />
                <Bar isAnimationActive={false} dataKey="totalScopeOfCustomer" fill="#eab308" name="Scope" />
                <Bar isAnimationActive={false} dataKey="totalBooking" fill="#8b5cf6" name="Booking" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm border" data-testid="requirement-flat-table">
                <thead>
                  <tr className="bg-teal-700 text-white">
                    <th className="px-3 py-2 text-left border">REQUIREMENT / CONFIGURATION</th>
                    <th className="px-3 py-2 text-right border">TOTAL LEAD</th>
                    <th className="px-3 py-2 text-right border">TOTAL OFFICE VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL PROJECT VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL SCOPE OF CUSTOMER</th>
                    <th className="px-3 py-2 text-right border">TOTAL BOOKING</th>
                  </tr>
                </thead>
                <tbody>
                  {requirementWiseData.length > 0 ? requirementWiseData.map((req, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                      <td className="px-3 py-2 border font-medium">{req.requirement || '-'}</td>
                      <td className="px-3 py-2 border text-right font-bold text-teal-700">{req.totalLead?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{req.totalOfficeVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{req.totalProjectVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{req.totalScopeOfCustomer?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right font-bold text-emerald-700">{req.totalBooking?.toLocaleString()}</td>
                    </tr>
                  )) : <tr><td colSpan="6" className="px-3 py-4 text-center text-stone-500">No data available</td></tr>}
                  {requirementWiseData.length > 0 && (
                    <tr className="bg-teal-100 font-bold">
                      <td className="px-3 py-2 border text-teal-800">GRAND TOTAL</td>
                      {['totalLead','totalOfficeVisit','totalProjectVisit','totalScopeOfCustomer','totalBooking'].map((k, ki) => (
                        <td key={ki} className={`px-3 py-2 border text-right ${k==='totalBooking'?'text-emerald-800':'text-teal-800'}`}>{requirementWiseData.reduce((a, b) => a + (b[k] || 0), 0).toLocaleString()}</td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Reference-wise View (flat projects) */}
        {dmDashboardView === 'reference_flat' && (
          <>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={referenceWiseData.filter(ref => ref.referenceName && ref.referenceName !== 'UNKNOWN').slice(0, 12).map(ref => ({
                name: (ref.referenceName || '-').split(' ').slice(0, 3).join(' '),
                totalLead: ref.totalLead, totalOfficeVisit: ref.totalOfficeVisit,
                totalProjectVisit: ref.totalProjectVisit, totalScopeOfCustomer: ref.totalScopeOfCustomer, totalBooking: ref.totalBooking
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={10} angle={-30} textAnchor="end" height={70} interval={0} />
                <YAxis /><Tooltip /><Legend />
                <Bar isAnimationActive={false} dataKey="totalLead" fill="#7e22ce" name="Total Lead" />
                <Bar isAnimationActive={false} dataKey="totalOfficeVisit" fill="#f97316" name="Office Visit" />
                <Bar isAnimationActive={false} dataKey="totalProjectVisit" fill="#15803d" name="Project Visit" />
                <Bar isAnimationActive={false} dataKey="totalScopeOfCustomer" fill="#eab308" name="Scope" />
                <Bar isAnimationActive={false} dataKey="totalBooking" fill="#8b5cf6" name="Booking" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm border" data-testid="reference-wise-table">
                <thead>
                  <tr className="bg-purple-700 text-white">
                    <th className="px-3 py-2 text-left border">REFERENCE SOURCE</th>
                    <th className="px-3 py-2 text-right border">TOTAL LEAD</th>
                    <th className="px-3 py-2 text-right border">TOTAL OFFICE VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL PROJECT VISIT</th>
                    <th className="px-3 py-2 text-right border">TOTAL SCOPE OF CUSTOMER</th>
                    <th className="px-3 py-2 text-right border">TOTAL BOOKING</th>
                  </tr>
                </thead>
                <tbody>
                  {referenceWiseData.length > 0 ? referenceWiseData.map((ref, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                      <td className="px-3 py-2 border font-medium">{ref.referenceName}</td>
                      <td className="px-3 py-2 border text-right font-bold text-purple-700">{ref.totalLead?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{ref.totalOfficeVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{ref.totalProjectVisit?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right">{ref.totalScopeOfCustomer?.toLocaleString()}</td>
                      <td className="px-3 py-2 border text-right font-bold text-emerald-700">{ref.totalBooking?.toLocaleString()}</td>
                    </tr>
                  )) : <tr><td colSpan="6" className="px-3 py-4 text-center text-stone-500">No data available</td></tr>}
                  {referenceWiseData.length > 0 && (
                    <tr className="bg-purple-100 font-bold">
                      <td className="px-3 py-2 border text-purple-800">GRAND TOTAL</td>
                      {['totalLead','totalOfficeVisit','totalProjectVisit','totalScopeOfCustomer','totalBooking'].map((k, ki) => (
                        <td key={ki} className={`px-3 py-2 border text-right ${k==='totalBooking'?'text-emerald-800':'text-purple-800'}`}>{referenceWiseData.reduce((a, b) => a + (b[k] || 0), 0).toLocaleString()}</td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MarketingDMTab;
